/**
 * v5/layouts/ListPageLayout.tsx
 *
 * A diverged copy of `components/layout/ListPageLayout.tsx`, kept because the routed
 * CRM pipeline page consumes this one and re-pointing it would be a page edit — and the
 * two copies have drifted, so re-pointing would change that page's behaviour rather
 * than just its imports.
 *
 * The narrow-width treatment matches the live copy: the filter panel leaves the flow
 * below `md`, the record detail below `lg`, controls for both sit in a sticky bar, and
 * at `lg` and above nothing changes. Read that file for why the boundaries differ.
 *
 * This copy also picks up the two scroll-lock relaxations the live one already had —
 * without them a phone cannot scroll this layout as a document at all.
 *
 * **Nothing keeps these two files in parity.** Later per-page work targets the live
 * components only, so a change made there has to be brought here by hand or this page
 * silently falls behind again.
 */
import { SidebarProvider } from "@/v5/components/ui/sidebar";
import { Filter, Loader2, PanelRightOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { useIsBelow } from "@/hooks/useBreakpoint";
import { OffCanvasPanel, OffCanvasRegion } from "@/components/layout/OffCanvasPanel";

interface ListPageLayoutProps {
  filterSidebar: React.ReactNode;
  children: React.ReactNode;
  rightPanel?: React.ReactNode;
  loading?: boolean;
}

export function ListPageLayout({
  filterSidebar,
  children,
  rightPanel,
  loading = false,
}: ListPageLayoutProps) {
  const filtersOffCanvas = useIsBelow("md");
  const detailOffCanvas = useIsBelow("lg");

  const hasFilters = filterSidebar != null;
  const hasDetail = rightPanel != null;

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const hadDetail = useRef(hasDetail);
  useEffect(() => {
    if (detailOffCanvas) {
      if (hasDetail && !hadDetail.current) setDetailOpen(true);
      else if (!hasDetail) setDetailOpen(false);
    }
    hadDetail.current = hasDetail;
  }, [hasDetail, detailOffCanvas]);

  useEffect(() => {
    if (!filtersOffCanvas) setFiltersOpen(false);
  }, [filtersOffCanvas]);

  useEffect(() => {
    if (!detailOffCanvas) setDetailOpen(false);
  }, [detailOffCanvas]);

  const showFiltersControl = filtersOffCanvas && hasFilters;
  const showDetailControl = detailOffCanvas && hasDetail;

  return (
    <OffCanvasRegion>
      <SidebarProvider
        // max-md: releases the viewport lock so this page scrolls as a document on a
        // phone, matching the live copy. md+ unchanged.
        className="h-full min-h-0 overflow-hidden max-md:h-auto max-md:overflow-visible"
        style={
          {
            "--sidebar-width": "16rem",
            "--sidebar-width-icon": "3.5rem",
          } as React.CSSProperties
        }
      >
        {!filtersOffCanvas && filterSidebar}

        <div className="flex-1 min-w-0 overflow-hidden flex flex-col max-md:overflow-visible">
          {(showFiltersControl || showDetailControl) && (
            <div className="sticky top-0 z-30 flex shrink-0 items-center gap-2 border-b border-border bg-white px-3 py-2">
              {showFiltersControl && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Filter className="h-4 w-4" />}
                  onClick={() => setFiltersOpen(true)}
                >
                  Filters
                </Button>
              )}

              {showDetailControl && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-auto"
                  leftIcon={<PanelRightOpen className="h-4 w-4" />}
                  onClick={() => setDetailOpen(true)}
                >
                  Details
                </Button>
              )}
            </div>
          )}

          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            children
          )}
        </div>

        {!detailOffCanvas && rightPanel}

        {showFiltersControl && (
          <OffCanvasPanel
            id="list-filters"
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            side="left"
            title="Filters"
          >
            {filterSidebar}
          </OffCanvasPanel>
        )}

        {showDetailControl && (
          <OffCanvasPanel
            id="list-detail"
            isOpen={detailOpen}
            onClose={() => setDetailOpen(false)}
            side="right"
            title="Details"
            size="wide"
          >
            {rightPanel}
          </OffCanvasPanel>
        )}
      </SidebarProvider>
    </OffCanvasRegion>
  );
}
